import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { apiFetch } from '../utils/api';

/** Selected state uses solid fills + white text so Meeting / Idea stay readable (not *-light). */
const entryTypeSelectedClass = {
  win: 'border-gold bg-gold text-white shadow-sm',
  setback: 'border-amber bg-amber text-white shadow-sm',
  meeting: 'border-teal bg-teal text-white shadow-sm',
  idea: 'border-lavender bg-lavender text-white shadow-sm',
  milestone: 'border-sage bg-sage text-white shadow-sm',
  neutral: 'border-text-primary bg-text-primary text-white shadow-sm',
};

const NewEntryPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { entryId } = useParams();
  const isEditing = Boolean(entryId);
  const entryTypeFromState = location.state?.entryType || 'neutral';

  const [formData, setFormData] = useState({
    date: new Date().toISOString().split('T')[0],
    title: '',
    content: '',
    entry_type: entryTypeFromState,
    energy_level: null,
    linked_goal_id: null,
    linked_person_id: null,
  });
  const [goals, setGoals] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loadingEntry, setLoadingEntry] = useState(isEditing);

  useEffect(() => {
    fetchGoalsAndContacts();
  }, []);

  useEffect(() => {
    if (!entryId) {
      setLoadingEntry(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const { entry } = await apiFetch(`/api/entries/${entryId}`);
        if (cancelled || !entry) return;
        const d = entry.date ? String(entry.date).slice(0, 10) : new Date().toISOString().split('T')[0];
        setFormData({
          date: d,
          title: entry.title || '',
          content: entry.body || '',
          entry_type: entry.entry_type || 'neutral',
          energy_level: entry.energy_level ?? null,
          linked_goal_id: entry.linked_goal_id ?? null,
          linked_person_id: entry.linked_person_id ?? null,
        });
      } catch (e) {
        console.error(e);
        toast.error(e?.message || 'Could not load entry');
        navigate('/log');
      } finally {
        if (!cancelled) setLoadingEntry(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [entryId, navigate]);
  
  const fetchGoalsAndContacts = async () => {
    try {
      const [goalsData, contactsData] = await Promise.all([
        apiFetch('/api/goals?status=active'),
        apiFetch('/api/orbit'),
      ]);
      
      setGoals(goalsData.goals || []);
      setContacts(contactsData.contacts || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (isEditing) {
        await apiFetch(`/api/entries/${entryId}`, {
          method: 'PUT',
          body: JSON.stringify(formData),
        });
        toast.success('Entry updated');
      } else {
        await apiFetch('/api/entries', {
          method: 'POST',
          body: JSON.stringify(formData),
        });
        toast.success('Entry saved successfully!');
      }
      navigate('/log');
    } catch (error) {
      console.error('Error saving entry:', error);
      toast.error(error?.message || 'Failed to save entry');
    } finally {
      setSaving(false);
    }
  };
  
  const typeOptions = [
    { value: 'win', label: '🏆 Win' },
    { value: 'setback', label: '⚡ Setback' },
    { value: 'meeting', label: '🤝 Meeting' },
    { value: 'idea', label: '💡 Idea' },
    { value: 'milestone', label: '🎯 Milestone' },
    { value: 'neutral', label: '📝 Note' },
  ];

  if (loadingEntry) {
    return (
      <div className="min-h-screen bg-bg-base p-6 lg:p-8 flex items-center justify-center" data-testid="new-entry-page">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-gold border-t-transparent rounded-full animate-spin mx-auto mb-4" />
          <p className="text-text-secondary">Loading entry…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bg-base p-6 lg:p-8" data-testid="new-entry-page">
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <h1 className="font-display text-4xl font-bold text-text-primary mb-2">
            {isEditing ? 'Edit Entry' : 'New Entry'}
          </h1>
          <p className="text-text-secondary">
            {isEditing ? 'Update this log entry' : 'Capture what happened today'}
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-bg-surface rounded-2xl p-8 border border-border shadow-sm space-y-6">
          {/* Entry Type */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">Entry Type</label>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {typeOptions.map((option) => {
                const selected = formData.entry_type === option.value;
                return (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, entry_type: option.value })}
                    className={`p-4 rounded-xl border-2 transition-all text-left ${
                      selected
                        ? entryTypeSelectedClass[option.value]
                        : 'border-border hover:border-gold bg-bg-surface text-text-primary'
                    }`}
                  >
                    <div className={`text-sm font-medium ${selected ? 'text-white' : 'text-text-primary'}`}>
                      {option.label}
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
          
          {/* Date */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Date</label>
            <input
              type="date"
              value={formData.date}
              onChange={(e) => setFormData({ ...formData, date: e.target.value })}
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              data-testid="entry-date"
            />
          </div>
          
          {/* Title */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">Title (optional)</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Give this entry a headline..."
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              data-testid="entry-title"
            />
          </div>
          
          {/* Content */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">What happened?</label>
            <textarea
              value={formData.content}
              onChange={(e) => setFormData({ ...formData, content: e.target.value })}
              placeholder="Write freely. This is yours."
              rows={8}
              maxLength={2000}
              required
              className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none resize-none"
              data-testid="entry-content"
            />
            <div className="text-xs text-text-tertiary text-right mt-1">
              {formData.content.length} / 2000
            </div>
          </div>
          
          {/* Energy Level */}
          <div>
            <label className="block text-sm font-medium text-text-primary mb-3">Your energy today</label>
            <div className="flex gap-3">
              {[1, 2, 3, 4, 5].map((level) => (
                <button
                  key={level}
                  type="button"
                  onClick={() => setFormData({ ...formData, energy_level: level })}
                  className={`w-12 h-12 rounded-full border-2 transition-all ${
                    formData.energy_level === level
                      ? 'border-gold bg-gold text-white scale-110'
                      : 'border-border hover:border-gold bg-bg-surface text-text-tertiary'
                  }`}
                >
                  {level}
                </button>
              ))}
            </div>
          </div>
          
          {/* Linked Goal */}
          {goals.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Linked goal (optional)</label>
              <select
                value={formData.linked_goal_id || ''}
                onChange={(e) => setFormData({ ...formData, linked_goal_id: e.target.value || null })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="">No goal linked</option>
                {goals.map((goal) => (
                  <option key={goal.id} value={goal.id}>{goal.title}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Linked Person */}
          {contacts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-text-primary mb-2">Who was involved? (optional)</label>
              <select
                value={formData.linked_person_id || ''}
                onChange={(e) => setFormData({ ...formData, linked_person_id: e.target.value || null })}
                className="w-full px-4 py-3 bg-bg-surface border border-border rounded-xl text-text-primary focus:border-gold focus:outline-none"
              >
                <option value="">No contact linked</option>
                {contacts.map((contact) => (
                  <option key={contact.id} value={contact.id}>{contact.name}</option>
                ))}
              </select>
            </div>
          )}
          
          {/* Actions */}
          <div className="flex gap-4 pt-4">
            <button
              type="button"
              onClick={() => navigate('/log')}
              className="px-6 py-3 border-2 border-border text-text-secondary rounded-xl hover:border-gold transition-all font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.content}
              className="flex-1 py-3 bg-gold hover:bg-gold-muted disabled:bg-text-tertiary/30 disabled:cursor-not-allowed text-white font-semibold rounded-xl transition-all shadow-sm"
              data-testid="save-entry-button"
            >
              {saving ? 'Saving...' : isEditing ? 'Save changes' : 'Save Entry'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default NewEntryPage;